import { TooltipProps } from '@mui/material';
import { cloneElement, ComponentPropsWithRef, HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import classes from './OverflowTooltip.module.scss';
import { NbTooltip } from './Tooltip';

interface OverflowTooltipProps {
  title?: string | React.ReactNode;
  parentContainerRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactElement;
  placement?: TooltipProps['placement'];
  slotProps?: TooltipProps['slotProps'];
}

export const OverflowTooltip = ({
  children,
  title,
  parentContainerRef,
  placement = 'bottom',
  slotProps,
}: OverflowTooltipProps) => {
  const [isOverflowed, setIsOverflow] = useState(false);
  const textElementRef = useRef<HTMLElement | null>(null);
  const [isWindowResized, setIsWindowResized] = useState(false);

  const hasAnyChildrenOverflowing = (element: HTMLElement) => {
    const elementOverflown = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
    if (elementOverflown) {
      return true;
    }

    const children = element.childNodes;
    for (let i = 0; i < children.length; i++) {
      if (children[i].nodeType === Node.ELEMENT_NODE) {
        const childElement = children[i] as HTMLElement;
        if (
          childElement.scrollWidth > childElement.clientWidth ||
          childElement.scrollHeight > childElement.clientHeight
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const compareSize = useCallback(() => {
    if (textElementRef.current) {
      const isOverflowing = hasAnyChildrenOverflowing(textElementRef.current);
      setIsOverflow(isOverflowing);

      if (isOverflowing) {
        textElementRef.current.childNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            (node as HTMLElement).style.overflow = 'hidden';
            (node as HTMLElement).style.textOverflow = 'ellipsis';
            (node as HTMLElement).style.display = 'inline';
            (node as HTMLElement).style.width = '100%';
          }
        });
      }
    }
  }, [setIsOverflow]);

  const onWindowResize = () => {
    setIsWindowResized(true);
  };

  // ResizeObserver works while editing grids using custom layouts
  // Also works when resizing window
  useEffect(() => {
    const currentContainerRef = parentContainerRef?.current;
    if (!currentContainerRef) {
      return;
    }

    const observer = new ResizeObserver(_ => {
      compareSize();
    });

    observer.observe(currentContainerRef);

    return () => observer.disconnect();
  }, [compareSize, parentContainerRef, title]);

  useEffect(() => {
    if (isWindowResized) {
      compareSize();
      setIsWindowResized(false);
    }
  }, [isWindowResized, compareSize]);

  // If parentContainer is defined then ResizeObserver will call compareSize()
  useEffect(() => {
    if (parentContainerRef) {
      return;
    }

    compareSize();
    window.addEventListener('resize', onWindowResize);

    return () => window.removeEventListener('resize', onWindowResize);
  }, [compareSize, parentContainerRef]);

  // From React 19 refs can be passed down directly: https://react.dev/blog/2024/12/05/react-19#ref-as-a-prop
  const childrenProps: ComponentPropsWithRef<any> = {
    ref: textElementRef,
    className: `${isOverflowed ? classes.root : ''} ${(children.props as HTMLAttributes<any>).className}`,
  };

  const clonedChildWithProps = cloneElement(children, childrenProps);

  return (
    <NbTooltip
      title={isOverflowed ? title : ''}
      placement={placement}
      slotProps={slotProps}
    >
      {clonedChildWithProps}
    </NbTooltip>
  );
};

export const NbOverflowTooltip = OverflowTooltip;
